import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Package, 
  ShoppingBag, 
  Layers, 
  Settings, 
  LogOut, 
  Plus, 
  Edit3, 
  Trash2, 
  Check, 
  Eye, 
  TrendingUp, 
  AlertTriangle, 
  PhoneCall, 
  MessageCircle,
  Save,
  X,
  Sparkles,
  RefreshCw,
  Upload,
  UploadCloud,
  ImagePlus,
  Tag as TagIcon,
  Star,
  Truck
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { ORDER_STATUSES, PAKISTAN_CITIES } from "@/constants/pakistanCities";
import apiClient, { resolveImageUrl } from "@/utils/api";

// --- Reusable single-image uploader for admin settings ---
function SingleImageUploader({ label, value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) onChange(data.url);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] uppercase tracking-wider font-bold text-stone-700 block">
        {label}
      </label>
      <div className="flex items-start gap-2">
        <div className="w-20 h-20 rounded-lg border border-stone-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <img src={resolveImageUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] text-stone-400 italic">No image</span>
          )}
        </div>
        <div className="flex-1 space-y-1">
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid={testId}
            />
            <span className="inline-block px-3 py-1.5 bg-white hover:bg-[#D4AF37]/10 border border-stone-300 rounded-lg text-xs font-semibold text-stone-800 transition">
              {uploading ? "Uploading…" : value ? "Replace image" : "Upload image"}
            </span>
          </label>
          {value ? (
            <button
              type="button"
              onClick={() => onChange("")}
              className="block text-[10px] font-semibold text-rose-700 hover:underline"
              data-testid={`${testId}-clear`}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// --- Admin Reviews Moderation Panel ---
function AdminReviewsPanel() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("afl_admin_token");
      const { data } = await apiClient.get(`/admin/reviews${status ? `?status_filter=${status}` : ""}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const act = async (id, newStatus) => {
    const token = localStorage.getItem("afl_admin_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      if (newStatus === "delete") {
        await apiClient.delete(`/admin/reviews/${id}`, { headers });
        toast.success("Review deleted");
      } else {
        await apiClient.patch(`/admin/reviews/${id}`, { status: newStatus }, { headers });
        toast.success(`Review ${newStatus}`);
      }
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="bg-white p-6 rounded-3xl border border-[#E5E0D8] shadow-luxury space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-lg font-bold text-stone-900">Customer Reviews Moderation</h2>
        <div className="flex gap-1 text-[10px] font-bold uppercase">
          {["pending","approved","rejected"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              data-testid={`admin-reviews-filter-${s}`}
              className={`px-3 py-1.5 rounded-lg border transition ${
                status === s ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white text-stone-600 border-stone-300 hover:border-stone-400"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-stone-400 italic">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="admin-reviews-empty" className="text-center text-xs text-stone-400 py-8">
          No {status} reviews.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-reviews-list">
          {items.map((r) => (
            <li key={r.id} data-testid={`admin-review-${r.id}`} className="bg-[#FAF8F5] border border-stone-200 rounded-2xl p-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-stone-900">{r.name}</span>
                  <span className="text-[10px] text-stone-500">rated {r.rating}/5</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-white border border-stone-200 text-stone-600">{r.product_name}</span>
                </div>
                <span className="text-[10px] text-stone-400">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              <p className="text-xs text-stone-700 whitespace-pre-wrap mb-2">{r.review_text}</p>
              <div className="flex flex-wrap gap-1.5">
                {r.status !== "approved" && (
                  <button data-testid={`admin-review-approve-${r.id}`} type="button" onClick={() => act(r.id, "approved")} className="px-3 py-1 text-[10px] font-bold rounded bg-emerald-600 text-white hover:bg-emerald-700">Approve</button>
                )}
                {r.status !== "rejected" && (
                  <button data-testid={`admin-review-reject-${r.id}`} type="button" onClick={() => act(r.id, "rejected")} className="px-3 py-1 text-[10px] font-bold rounded bg-amber-600 text-white hover:bg-amber-700">Reject</button>
                )}
                <button data-testid={`admin-review-delete-${r.id}`} type="button" onClick={() => act(r.id, "delete")} className="px-3 py-1 text-[10px] font-bold rounded bg-rose-100 text-rose-700 hover:bg-rose-200">Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { adminUser, isAuthenticated, loading: authLoading, logout } = useAuth();
  const { settings, reloadSettings } = useSiteSettings();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview"); // "overview", "products", "orders", "wholesale", "categories", "settings"
  const [metrics, setMetrics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);

  // Modals & Edit States
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "Diamond Zari Sitara Lace",
    description: "",
    wholesale_price: 2500,
    retail_price: 3500,
    stock_status: "In Stock",
    stock_quantity: 50,
    min_wholesale_qty: 5,
    is_complete_set: true,
    available_colors: "",
    design_code: "",
    complete_set: {
      total_colors: 0,
      taan_count: 0,
      gaz_per_taan: 0,
      total_gaz: 0,
      min_order_sets: 1,
      notes: "",
    },
    images: [],
    pricing_tiers: [],
    weight_kg: 0.5,
    featured: false,
    sku: ""
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState(settings);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/admin/login");
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllAdminData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (settings) {
      setSettingsForm(settings);
    }
  }, [settings]);

  const fetchAllAdminData = async () => {
    setLoadingData(true);
    setLoadErrors([]);
    const token = localStorage.getItem("afl_admin_token");
    const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

    const endpoints = [
      { key: "metrics", url: "/admin/metrics" },
      { key: "products", url: "/products" },
      { key: "orders", url: "/admin/orders" },
      { key: "inquiries", url: "/admin/wholesale-inquiries" },
      { key: "categories", url: "/categories" },
    ];

    const results = await Promise.allSettled(
      endpoints.map((e) => apiClient.get(e.url, authHeaders))
    );

    const errors = [];
    results.forEach((res, idx) => {
      const key = endpoints[idx].key;
      if (res.status === "fulfilled" && res.value?.data !== undefined) {
        if (key === "metrics") setMetrics(res.value.data);
        else if (key === "products") setProducts(res.value.data || []);
        else if (key === "orders") setOrders(res.value.data || []);
        else if (key === "inquiries") setInquiries(res.value.data || []);
        else if (key === "categories") setCategories(res.value.data || []);
      } else {
        const status = res.reason?.response?.status;
        const detail = res.reason?.response?.data?.detail || res.reason?.message || "Request failed";
        errors.push({ key, status, detail });
        console.error(`Admin data load failed for ${key}:`, res.reason);
      }
    });

    if (errors.length > 0) {
      setLoadErrors(errors);
      const authError = errors.find((e) => e.status === 401 || e.status === 403);
      if (authError) {
        toast.error("Admin session expired. Please log in again.");
      } else {
        toast.error(`Failed to load: ${errors.map((e) => e.key).join(", ")}`);
      }
    }

    setLoadingData(false);
  };

  // Open Product Modal
  const handleOpenAddProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      category: categories[0]?.name || "Diamond Zari Sitara Lace",
      description: "",
      wholesale_price: 2500,
      retail_price: 3500,
      stock_status: "In Stock",
      stock_quantity: 50,
      min_wholesale_qty: 5,
      is_complete_set: true,
      available_colors: "",
      design_code: "",
      complete_set: {
        total_colors: 0,
        taan_count: 0,
        gaz_per_taan: 0,
        total_gaz: 0,
        min_order_sets: 1,
        notes: "",
      },
      images: [],
      pricing_tiers: [],
      weight_kg: 0.5,
      featured: false,
      sku: `AFL-${Math.floor(100 + Math.random() * 900)}`
    });
    setProductModalOpen(true);
  };

  const handleOpenEditProduct = (prod) => {
    setEditingProduct(prod);
    setFormData({
      name: prod.name,
      category: prod.category,
      description: prod.description,
      wholesale_price: prod.wholesale_price,
      retail_price: prod.retail_price,
      stock_status: prod.stock_status,
      stock_quantity: prod.stock_quantity,
      min_wholesale_qty: prod.min_wholesale_qty || 5,
      is_complete_set: prod.is_complete_set,
      available_colors: (prod.available_colors || []).join(", "),
      design_code: prod.design_code || "",
      complete_set: {
        total_colors: prod.complete_set?.total_colors || 0,
        taan_count: prod.complete_set?.taan_count || 0,
        gaz_per_taan: prod.complete_set?.gaz_per_taan || 0,
        total_gaz: prod.complete_set?.total_gaz || 0,
        min_order_sets: prod.complete_set?.min_order_sets || 1,
        notes: prod.complete_set?.notes || "",
      },
      images: prod.images || [],
      pricing_tiers: prod.pricing_tiers || [],
      weight_kg: prod.weight_kg ?? 0.5,
      featured: prod.featured || false,
      sku: prod.sku || ""
    });
    setProductModalOpen(true);
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploadingImage(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await apiClient.post("/admin/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.url) uploaded.push(data.url);
      } catch (err) {
        console.error("Upload failed for", file.name, err);
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    if (uploaded.length) {
      setFormData((prev) => ({ ...prev, images: [...(prev.images || []), ...uploaded] }));
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
    }
    setUploadingImage(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const handleRemoveImage = (idx) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx),
    }));
  };

  const handleAddTier = () => {
    setFormData((prev) => ({
      ...prev,
      pricing_tiers: [
        ...(prev.pricing_tiers || []),
        { min_quantity: 10, price_per_set: prev.wholesale_price || 2000, label: "" },
      ],
    }));
  };

  const handleUpdateTier = (idx, field, value) => {
    setFormData((prev) => {
      const tiers = [...(prev.pricing_tiers || [])];
      tiers[idx] = { ...tiers[idx], [field]: field === "label" ? value : Number(value) || 0 };
      return { ...prev, pricing_tiers: tiers };
    });
  };

  const handleRemoveTier = (idx) => {
    setFormData((prev) => ({
      ...prev,
      pricing_tiers: (prev.pricing_tiers || []).filter((_, i) => i !== idx),
    }));
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        wholesale_price: parseFloat(formData.wholesale_price),
        retail_price: parseFloat(formData.retail_price),
        stock_status: formData.stock_status,
        stock_quantity: parseInt(formData.stock_quantity, 10),
        min_wholesale_qty: parseInt(formData.min_wholesale_qty, 10),
        is_complete_set: formData.is_complete_set,
        available_colors: formData.available_colors.split(",").map((s) => s.trim()).filter(Boolean),
        design_code: (formData.design_code || "").trim(),
        complete_set: {
          total_colors: parseInt(formData.complete_set.total_colors, 10) || 0,
          taan_count: parseInt(formData.complete_set.taan_count, 10) || 0,
          gaz_per_taan: parseFloat(formData.complete_set.gaz_per_taan) || 0,
          total_gaz: parseFloat(formData.complete_set.total_gaz) || 0,
          min_order_sets: parseInt(formData.complete_set.min_order_sets, 10) || 1,
          notes: formData.complete_set.notes || "",
        },
        images: (formData.images || []).filter(Boolean),
        pricing_tiers: (formData.pricing_tiers || [])
          .filter((t) => Number(t.min_quantity) > 0 && Number(t.price_per_set) > 0)
          .map((t) => ({
            min_quantity: parseInt(t.min_quantity, 10),
            price_per_set: parseFloat(t.price_per_set),
            label: t.label || `${t.min_quantity}+ Sets`,
          })),
        weight_kg: parseFloat(formData.weight_kg) || 0.5,
        featured: formData.featured,
        sku: formData.sku
      };

      if (editingProduct) {
        await apiClient.put(`/admin/products/${editingProduct.id}`, payload);
        toast.success("Product updated successfully!");
      } else {
        await apiClient.post("/admin/products", payload);
        toast.success("New product created successfully!");
      }
      setProductModalOpen(false);
      fetchAllAdminData();
    } catch (err) {
      console.error("Product save error", err);
      toast.error("Failed to save product.");
    }
  };

  const handleDeleteProduct = async (prodId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await apiClient.delete(`/admin/products/${prodId}`);
      toast.success("Product deleted.");
      fetchAllAdminData();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const handleOrderStatusUpdate = async (orderId, newStatus) => {
    try {
      await apiClient.patch(`/admin/orders/${orderId}/status`, { order_status: newStatus });
      toast.success(`Order status updated to ${newStatus}`);
      fetchAllAdminData();
    } catch (e) {
      toast.error("Failed to update status.");
    }
  };

  const [postexBooking, setPostexBooking] = useState(null);
  const handleSendToPostEx = async (orderNumber) => {
    if (!window.confirm(
      `Book a PostEx shipment for order ${orderNumber}?\n\n` +
      `This will call PostEx production API and consume 1 shipment slot on your merchant account.`
    )) return;
    try {
      setPostexBooking(orderNumber);
      const { data } = await apiClient.post(`/admin/orders/${orderNumber}/postex-shipment`);
      toast.success(`PostEx shipment booked. Tracking # ${data.tracking_number}`);
      fetchAllAdminData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "PostEx shipment failed.";
      if (typeof detail === "string") msg = detail;
      else if (detail?.error === "already_booked") msg = `Already booked (Tracking # ${detail.tracking_number}).`;
      else if (detail?.postex_status_message) msg = `PostEx: ${detail.postex_status_message}`;
      else if (detail?.error) msg = `PostEx: ${detail.error}`;
      toast.error(msg);
    } finally {
      setPostexBooking(null);
    }
  };

  const handleInquiryStatusUpdate = async (inquiryId, newStatus) => {
    try {
      await apiClient.patch(`/admin/wholesale-inquiries/${inquiryId}/status`, { status: newStatus });
      toast.success("Inquiry status updated.");
      fetchAllAdminData();
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put("/admin/settings", settingsForm);
      toast.success("Business & Payment settings updated successfully!");
      reloadSettings();
    } catch (e) {
      toast.error("Failed to update settings.");
    }
  };

  if (authLoading || !isAuthenticated) {
    return <div className="p-20 text-center text-xs text-stone-400">Loading admin session...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      
      {/* Top Bar */}
      <div className="bg-[#1C1917] text-white p-6 rounded-3xl border-2 border-[#D4AF37] flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="text-xs uppercase tracking-widest text-[#E6C687] font-semibold">
              Live Admin Control Panel
            </span>
          </div>
          <h1 data-testid="admin-dashboard-title" className="font-heading text-2xl sm:text-3xl font-bold text-white">
            Anwer Fancy Lace Dashboard
          </h1>
          <p className="text-xs text-stone-400">
            Logged in as: <strong className="text-stone-200">{adminUser?.email || "Admin"}</strong>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={fetchAllAdminData}
            data-testid="admin-refresh-btn"
            className="p-2.5 bg-stone-800 hover:bg-stone-700 text-stone-300 rounded-xl transition"
            title="Refresh Data"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="px-4 py-2.5 bg-rose-900/80 hover:bg-rose-800 text-rose-100 text-xs font-bold rounded-xl flex items-center gap-1.5 transition"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex bg-white p-1.5 rounded-2xl border border-[#E5E0D8] shadow-sm overflow-x-auto gap-1 text-xs font-semibold">
        <button
          onClick={() => setActiveTab("overview")}
          data-testid="admin-tab-overview"
          className={`px-4 py-2.5 rounded-xl transition whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "overview" ? "bg-[#1C1917] text-white shadow-sm" : "text-stone-600 hover:bg-[#FAF8F5]"
          }`}
        >
          <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
          <span>Overview Metrics</span>
        </button>

        <button
          onClick={() => setActiveTab("products")}
          data-testid="admin-tab-products"
          className={`px-4 py-2.5 rounded-xl transition whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "products" ? "bg-[#1C1917] text-white shadow-sm" : "text-stone-600 hover:bg-[#FAF8F5]"
          }`}
        >
          <Package className="w-4 h-4 text-[#D4AF37]" />
          <span>Products & Sets ({products.length})</span>
        </button>

        <button
          onClick={() => setActiveTab("orders")}
          data-testid="admin-tab-orders"
          className={`px-4 py-2.5 rounded-xl transition whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "orders" ? "bg-[#1C1917] text-white shadow-sm" : "text-stone-600 hover:bg-[#FAF8F5]"
          }`}
        >
          <ShoppingBag className="w-4 h-4 text-[#D4AF37]" />
          <span>Customer Orders ({orders.length})</span>
          {(metrics?.pending_orders || 0) > 0 && (
            <span className="bg-amber-500 text-white text-[10px] px-1.5 py-0.2 rounded-full">
              {metrics.pending_orders}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab("wholesale")}
          data-testid="admin-tab-wholesale"
          className={`px-4 py-2.5 rounded-xl transition whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "wholesale" ? "bg-[#1C1917] text-white shadow-sm" : "text-stone-600 hover:bg-[#FAF8F5]"
          }`}
        >
          <Layers className="w-4 h-4 text-[#D4AF37]" />
          <span>Wholesale Inquiries ({inquiries.length})</span>
        </button>

        <button
          onClick={() => setActiveTab("reviews")}
          data-testid="admin-tab-reviews"
          className={`px-4 py-2.5 rounded-xl transition whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "reviews" ? "bg-[#1C1917] text-white shadow-sm" : "text-stone-600 hover:bg-[#FAF8F5]"
          }`}
        >
          <Star className="w-4 h-4 text-[#D4AF37]" />
          <span>Reviews</span>
        </button>

        <button
          onClick={() => setActiveTab("settings")}
          data-testid="admin-tab-settings"
          className={`px-4 py-2.5 rounded-xl transition whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "settings" ? "bg-[#1C1917] text-white shadow-sm" : "text-stone-600 hover:bg-[#FAF8F5]"
          }`}
        >
          <Settings className="w-4 h-4 text-[#D4AF37]" />
          <span>Payment & Store Settings</span>
        </button>
      </div>

      {/* TAB CONTENT: 1. OVERVIEW */}
      {activeTab === "overview" && (
        <div className="space-y-8">
          {loadErrors.length > 0 && (
            <div data-testid="admin-load-error-banner" className="bg-rose-50 border border-rose-200 text-rose-900 rounded-2xl p-4 text-xs">
              <strong className="font-bold block mb-1">Could not load some sections:</strong>
              <ul className="list-disc list-inside space-y-0.5">
                {loadErrors.map((e) => (
                  <li key={e.key}>
                    <span className="font-semibold capitalize">{e.key}</span>: {e.detail}
                    {e.status ? ` (HTTP ${e.status})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            
            <div className="bg-white p-5 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Total Revenue (PKR)</span>
              <div data-testid="metric-total-revenue" className="font-heading text-2xl font-bold text-stone-900">
                {loadingData || !metrics ? (
                  <span className="inline-block h-6 w-24 bg-stone-100 rounded animate-pulse" aria-label="Loading" />
                ) : (
                  <>Rs. {metrics.total_revenue_pkr?.toLocaleString() || 0}</>
                )}
              </div>
              <span className="text-[10px] text-emerald-700 font-semibold">Processed / COD Orders</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Total Customer Orders</span>
              <div data-testid="metric-total-orders" className="font-heading text-2xl font-bold text-stone-900">
                {loadingData || !metrics ? (
                  <span className="inline-block h-6 w-12 bg-stone-100 rounded animate-pulse" aria-label="Loading" />
                ) : (
                  metrics.total_orders
                )}
              </div>
              <span className="text-[10px] text-amber-700 font-semibold">
                {loadingData || !metrics ? "—" : `${metrics.pending_orders} pending dispatch`}
              </span>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Live Lace Products</span>
              <div data-testid="metric-total-products" className="font-heading text-2xl font-bold text-stone-900">
                {loadingData || !metrics ? (
                  <span className="inline-block h-6 w-12 bg-stone-100 rounded animate-pulse" aria-label="Loading" />
                ) : (
                  metrics.total_products
                )}
              </div>
              <span className="text-[10px] text-stone-500">In 10 specialized categories</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Wholesale Leads</span>
              <div data-testid="metric-wholesale-leads" className="font-heading text-2xl font-bold text-stone-900">
                {loadingData || !metrics ? (
                  <span className="inline-block h-6 w-12 bg-stone-100 rounded animate-pulse" aria-label="Loading" />
                ) : (
                  metrics.wholesale_inquiries
                )}
              </div>
              <span className="text-[10px] text-[#996515] font-semibold">Direct boutique inquiries</span>
            </div>

          </div>

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-3xl border border-[#E5E0D8] shadow-sm space-y-4">
            <h3 className="font-heading font-bold text-base text-stone-900">Quick Store Actions</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleOpenAddProduct}
                data-testid="admin-quick-add-product"
                className="px-4 py-2.5 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-sm transition flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                <span>Add New Lace / Complete Set</span>
              </button>
              <button
                onClick={() => setActiveTab("orders")}
                className="px-4 py-2.5 bg-[#FAF8F5] hover:bg-stone-200 text-stone-800 text-xs font-bold rounded-xl border border-stone-300 transition"
              >
                View Pending Orders ({metrics?.pending_orders ?? 0})
              </button>
              <button
                onClick={() => setActiveTab("wholesale")}
                className="px-4 py-2.5 bg-[#FAF8F5] hover:bg-stone-200 text-stone-800 text-xs font-bold rounded-xl border border-stone-300 transition"
              >
                View Wholesale Inquiries ({metrics?.wholesale_inquiries ?? 0})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: 2. PRODUCTS */}
      {activeTab === "products" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="font-heading text-xl font-bold text-stone-900">
              Product & Complete Set Inventory ({products.length})
            </h2>
            <button
              type="button"
              onClick={handleOpenAddProduct}
              data-testid="admin-add-product-btn"
              className="px-4 py-2.5 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              <span>Add New Lace Product</span>
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm overflow-x-auto">
            <table className="w-full text-left text-xs text-stone-700">
              <thead className="bg-[#FAF8F5] text-stone-800 border-b border-[#E5E0D8] uppercase text-[10px] tracking-wider font-bold">
                <tr>
                  <th className="p-4">Product</th>
                  <th className="p-4">Category</th>
                  <th className="p-4">Retail Price (PKR)</th>
                  <th className="p-4">Wholesale Price</th>
                  <th className="p-4">Stock Status</th>
                  <th className="p-4">Set Type</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="hover:bg-[#FAF8F5]/80 transition">
                    <td className="p-4 flex items-center gap-3">
                      <img src={resolveImageUrl(p.images?.[0])} alt={p.name} className="w-10 h-10 rounded-lg object-cover border border-stone-200 shrink-0" />
                      <div>
                        <span className="font-bold text-stone-900 block">{p.name}</span>
                        <span className="text-[10px] text-stone-400">SKU: {p.sku || p.id}</span>
                      </div>
                    </td>
                    <td className="p-4 font-medium text-[#996515]">{p.category}</td>
                    <td className="p-4 font-bold text-stone-900">Rs. {p.retail_price?.toLocaleString()}</td>
                    <td className="p-4 text-stone-600">Rs. {p.wholesale_price?.toLocaleString()}</td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        p.stock_status === "In Stock" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                      }`}>
                        {p.stock_status} ({p.stock_quantity})
                      </span>
                    </td>
                    <td className="p-4">
                      {p.is_complete_set ? (
                        <span className="text-[10px] font-bold text-[#996515] bg-[#D4AF37]/15 px-2 py-0.5 rounded">
                          Complete Set
                        </span>
                      ) : (
                        <span className="text-[10px] text-stone-500">Loose Trim</span>
                      )}
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <button
                        onClick={() => handleOpenEditProduct(p)}
                        data-testid={`btn-edit-prod-${p.id}`}
                        className="p-1.5 text-stone-600 hover:text-[#996515] hover:bg-stone-100 rounded-lg transition"
                        title="Edit Product"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(p.id)}
                        data-testid={`btn-delete-prod-${p.id}`}
                        className="p-1.5 text-stone-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        title="Delete Product"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: 3. ORDERS */}
      {activeTab === "orders" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-bold text-stone-900">
            Customer Orders & Deliveries ({orders.length})
          </h2>

          <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm overflow-x-auto">
            <table className="w-full text-left text-xs text-stone-700">
              <thead className="bg-[#FAF8F5] text-stone-800 border-b border-[#E5E0D8] uppercase text-[10px] tracking-wider font-bold">
                <tr>
                  <th className="p-4">Order ID & Date</th>
                  <th className="p-4">Customer</th>
                  <th className="p-4">City & Address</th>
                  <th className="p-4">Items / Sets</th>
                  <th className="p-4">Amount & Payment</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">PostEx Shipment</th>
                  <th className="p-4 text-right">Quick Contact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {orders.map((o) => (
                  <tr key={o.id || o.order_number} data-testid={`order-row-${o.order_number}`} className="hover:bg-[#FAF8F5]/80 transition">
                    <td className="p-4">
                      <span className="font-bold text-stone-900 block">{o.order_number}</span>
                      <span className="text-[10px] text-stone-400">{new Date(o.created_at).toLocaleDateString()}</span>
                    </td>
                    <td className="p-4">
                      <span className="font-bold text-stone-900 block">{o.customer_name}</span>
                      <span className="text-[10px] text-stone-500">{o.mobile_number}</span>
                    </td>
                    <td className="p-4">
                      <span className="font-semibold text-stone-800 block">{o.city}</span>
                      <span className="text-[10px] text-stone-500 line-clamp-1 max-w-xs">{o.delivery_address}</span>
                    </td>
                    <td className="p-4">
                      <span className="font-bold text-stone-900">{o.items?.length || 0} Sets</span>
                      <span className="text-[10px] text-stone-500 block truncate max-w-xs">
                        {o.items?.map(it => it.product_name).join(", ")}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="font-bold text-[#996515] block">Rs. {o.total_amount?.toLocaleString()}</span>
                      <span className="text-[10px] uppercase font-semibold text-stone-500">{o.payment_method}</span>
                    </td>
                    <td className="p-4">
                      <select
                        value={o.order_status}
                        onChange={(e) => handleOrderStatusUpdate(o.order_number, e.target.value)}
                        data-testid={`order-status-select-${o.order_number}`}
                        className="bg-[#FAF8F5] border border-stone-300 text-xs rounded-lg px-2 py-1 font-semibold focus:outline-none focus:border-[#D4AF37]"
                      >
                        {ORDER_STATUSES.map((st) => (
                          <option key={st.value} value={st.value}>{st.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4">
                      {o.tracking_number ? (
                        <div
                          data-testid={`postex-tracking-cell-${o.order_number}`}
                          className="space-y-1"
                        >
                          <div className="flex items-center gap-1">
                            <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-300 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">
                              Booked
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard?.writeText(o.tracking_number);
                                toast.success("Tracking number copied");
                              }}
                              className="text-[10px] font-mono font-bold text-[#996515] underline decoration-dotted hover:text-[#1C1917]"
                              data-testid={`postex-tracking-number-${o.order_number}`}
                              title="Click to copy"
                            >
                              {o.tracking_number}
                            </button>
                          </div>
                          {o.postex_order_status && (
                            <span className="text-[10px] text-stone-500 block">
                              PostEx: {o.postex_order_status}
                            </span>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          data-testid={`send-to-postex-btn-${o.order_number}`}
                          disabled={postexBooking === o.order_number}
                          onClick={() => handleSendToPostEx(o.order_number)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-[10px] font-bold uppercase tracking-wider transition disabled:opacity-60"
                        >
                          <Truck className="w-3 h-3" />
                          <span>{postexBooking === o.order_number ? "Booking…" : "Send to PostEx"}</span>
                        </button>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <a
                        href={`https://wa.me/${o.whatsapp_number ? o.whatsapp_number.replace(/[^0-9]/g, '') : o.mobile_number.replace(/[^0-9]/g, '')}?text=Assalam%20o%20Alaikum%20${encodeURIComponent(o.customer_name)},%20we%20are%20contacting%20you%20regarding%20your%20Anwer%20Fancy%20Lace%20order%20(${o.order_number}).`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-[11px] font-bold"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>WhatsApp</span>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: 4. WHOLESALE INQUIRIES */}
      {activeTab === "wholesale" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-bold text-stone-900">
            Wholesale Leads & Bulk Inquiries ({inquiries.length})
          </h2>

          <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm overflow-x-auto">
            <table className="w-full text-left text-xs text-stone-700">
              <thead className="bg-[#FAF8F5] text-stone-800 border-b border-[#E5E0D8] uppercase text-[10px] tracking-wider font-bold">
                <tr>
                  <th className="p-4">Inquiry Ref & Date</th>
                  <th className="p-4">Business & Contact</th>
                  <th className="p-4">City</th>
                  <th className="p-4">Volume & Categories</th>
                  <th className="p-4">Message</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {inquiries.map((iq) => (
                  <tr key={iq.id || iq.inquiry_number} data-testid={`inquiry-row-${iq.inquiry_number}`} className="hover:bg-[#FAF8F5]/80 transition">
                    <td className="p-4">
                      <span className="font-bold text-stone-900 block">{iq.inquiry_number}</span>
                      <span className="text-[10px] text-stone-400">{new Date(iq.created_at).toLocaleDateString()}</span>
                    </td>
                    <td className="p-4">
                      <span className="font-bold text-stone-900 block">{iq.business_name}</span>
                      <span className="text-stone-500">{iq.contact_person} ({iq.mobile_number})</span>
                    </td>
                    <td className="p-4 font-semibold text-stone-800">{iq.city}</td>
                    <td className="p-4">
                      <span className="font-bold text-[#996515] block">{iq.estimated_monthly_volume}</span>
                      <span className="text-[10px] text-stone-500">{(iq.product_interests || []).join(", ")}</span>
                    </td>
                    <td className="p-4 max-w-xs truncate text-stone-600">{iq.message}</td>
                    <td className="p-4">
                      <select
                        value={iq.status}
                        onChange={(e) => handleInquiryStatusUpdate(iq.inquiry_number, e.target.value)}
                        className="bg-[#FAF8F5] border border-stone-300 text-xs rounded-lg px-2 py-1 font-semibold focus:outline-none focus:border-[#D4AF37]"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="quotation_sent">Quotation Sent</option>
                        <option value="deal_closed">Deal Closed</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      {(() => {
                        const raw = iq.whatsapp_number || iq.mobile_number || "";
                        const d = raw.replace(/[^0-9]/g, "");
                        let intl = d;
                        if (d.startsWith("92") && d.length === 12) intl = d;
                        else if (d.startsWith("0") && d.length === 11) intl = "92" + d.slice(1);
                        else if (d.length === 10 && d.startsWith("3")) intl = "92" + d;
                        else intl = "92" + d.slice(-10);
                        const msg = encodeURIComponent(
                          `Assalam-o-Alaikum, I am contacting you regarding your wholesale inquiry from Anwer Fancy Lace. Inquiry ID: ${iq.inquiry_number || iq.id || ""}.`
                        );
                        return (
                          <a
                            href={`https://wa.me/${intl}?text=${msg}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`admin-wholesale-wa-${iq.id || iq.inquiry_number}`}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-[11px] font-bold"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            <span>WhatsApp Lead</span>
                          </a>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: REVIEWS MODERATION */}
      {activeTab === "reviews" && (
        <AdminReviewsPanel />
      )}

      {/* TAB CONTENT: 5. STORE SETTINGS */}
      {activeTab === "settings" && (
        <form onSubmit={handleSaveSettings} className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E5E0D8] shadow-luxury space-y-6 max-w-3xl">
          <div className="border-b border-[#E5E0D8] pb-4">
            <h2 className="font-heading text-xl font-bold text-stone-900">
              Payment & Business Information Settings
            </h2>
            <p className="text-xs text-stone-500 mt-1">
              Update editable placeholders for bank account numbers, EasyPaisa, JazzCash, delivery rates, and phone numbers shown on checkout.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            
            <div className="space-y-1">
              <label className="font-semibold text-stone-800">Brand Name</label>
              <input
                type="text"
                value={settingsForm.brand_name || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, brand_name: e.target.value })}
                data-testid="admin-settings-brand-name"
                className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
              />
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-stone-800">WhatsApp / Contact Number</label>
              <input
                type="text"
                value={settingsForm.whatsapp_number || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, whatsapp_number: e.target.value })}
                data-testid="admin-settings-whatsapp"
                className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
              />
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-stone-800">Business Phone</label>
              <input
                type="text"
                value={settingsForm.phone_number || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, phone_number: e.target.value })}
                data-testid="admin-settings-phone"
                className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
              />
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-stone-800">Business Email</label>
              <input
                type="email"
                value={settingsForm.email || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })}
                data-testid="admin-settings-email"
                className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
              />
            </div>

            <div className="sm:col-span-2 space-y-1">
              <label className="font-semibold text-stone-800">Physical Address</label>
              <input
                type="text"
                value={settingsForm.address || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, address: e.target.value })}
                data-testid="admin-settings-address"
                className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
              />
            </div>

            <div className="sm:col-span-2 space-y-1">
              <label className="font-semibold text-stone-800">Header Announcement Banner</label>
              <input
                type="text"
                value={settingsForm.announcement_text || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, announcement_text: e.target.value })}
                data-testid="admin-settings-announcement"
                placeholder="e.g. Free COD on orders above Rs 5,000"
                className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
              />
            </div>

            {/* Home & About Images */}
            <div className="sm:col-span-2 p-4 bg-[#FAF8F5] rounded-xl border border-stone-200 space-y-3">
              <h4 className="font-bold text-stone-900 text-xs">Home Hero Slider & Section Images</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SingleImageUploader
                  label="Home Hero Slider — Image 1"
                  value={settingsForm.hero_image_1 || ""}
                  onChange={(v) => setSettingsForm({ ...settingsForm, hero_image_1: v })}
                  testId="admin-settings-hero-image-1"
                />
                <SingleImageUploader
                  label="Home Hero Slider — Image 2"
                  value={settingsForm.hero_image_2 || ""}
                  onChange={(v) => setSettingsForm({ ...settingsForm, hero_image_2: v })}
                  testId="admin-settings-hero-image-2"
                />
                <SingleImageUploader
                  label="Wholesale Section Image"
                  value={settingsForm.wholesale_section_image || ""}
                  onChange={(v) => setSettingsForm({ ...settingsForm, wholesale_section_image: v })}
                  testId="admin-settings-wholesale-image"
                />
                <SingleImageUploader
                  label="About Us Page Image"
                  value={settingsForm.about_us_image || ""}
                  onChange={(v) => setSettingsForm({ ...settingsForm, about_us_image: v })}
                  testId="admin-settings-about-image"
                />
              </div>
            </div>

            {/* Homepage Story Hero — brand-story reel + Featured This Week pin */}
            <div className="sm:col-span-2 p-4 bg-[#FAF8F5] rounded-xl border border-stone-200 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="font-bold text-stone-900 text-xs">Homepage Story Hero</h4>
                  <p className="text-[10px] text-stone-500 italic mt-0.5">
                    Turn the hero into a mini brand-story reel. Each hero image pairs with its own caption. Pick a product to pin as "Featured This Week".
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-stone-600 block">
                    Eyebrow badge (small text above hero title)
                  </label>
                  <input
                    type="text"
                    data-testid="admin-settings-hero-eyebrow"
                    value={settingsForm.hero_story_eyebrow || ""}
                    onChange={(e) => setSettingsForm({ ...settingsForm, hero_story_eyebrow: e.target.value })}
                    placeholder="e.g. Original China Imported Laces"
                    className="w-full px-2 py-1.5 bg-white border border-stone-300 rounded-lg text-xs"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-stone-600 block">
                    Story caption — paired with Hero Image 1
                  </label>
                  <input
                    type="text"
                    data-testid="admin-settings-hero-story-1"
                    value={settingsForm.hero_story_caption_1 || ""}
                    onChange={(e) => setSettingsForm({ ...settingsForm, hero_story_caption_1: e.target.value })}
                    placeholder="e.g. Curated for Bridal Season 2026"
                    className="w-full px-2 py-1.5 bg-white border border-stone-300 rounded-lg text-xs"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-stone-600 block">
                    Story caption — paired with Hero Image 2
                  </label>
                  <input
                    type="text"
                    data-testid="admin-settings-hero-story-2"
                    value={settingsForm.hero_story_caption_2 || ""}
                    onChange={(e) => setSettingsForm({ ...settingsForm, hero_story_caption_2: e.target.value })}
                    placeholder="e.g. Fresh Sitara Arrivals — Direct China Import"
                    className="w-full px-2 py-1.5 bg-white border border-stone-300 rounded-lg text-xs"
                  />
                </div>
              </div>

              <div className="mt-2 p-3 bg-white rounded-lg border border-[#D4AF37]/40 space-y-2.5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#D4AF37]/15 text-[#996515] text-[10px] font-bold uppercase tracking-wider">
                    Featured This Week
                  </span>
                  <span className="text-[10px] text-stone-500">Pinned on the Home hero</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-stone-600 block">Pin label</label>
                    <input
                      type="text"
                      data-testid="admin-settings-featured-tagline"
                      value={settingsForm.featured_this_week_tagline || ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, featured_this_week_tagline: e.target.value })}
                      placeholder="Featured This Week"
                      className="w-full px-2 py-1.5 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-stone-600 block">Subtitle</label>
                    <input
                      type="text"
                      data-testid="admin-settings-featured-subtitle"
                      value={settingsForm.featured_this_week_subtitle || ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, featured_this_week_subtitle: e.target.value })}
                      placeholder="e.g. Editor's Pick — Limited Stock"
                      className="w-full px-2 py-1.5 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-stone-600 block">Pinned Product</label>
                    <select
                      data-testid="admin-settings-featured-product"
                      value={settingsForm.featured_this_week_product_id || ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, featured_this_week_product_id: e.target.value })}
                      className="w-full px-2 py-1.5 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs"
                    >
                      <option value="">— None (hide pin) —</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-[10px] text-stone-500 italic">
                  Leave "Pinned Product" empty to hide the Featured This Week card entirely.
                </p>
              </div>
            </div>

            {/* Category Images */}
            <div className="sm:col-span-2 p-4 bg-[#FAF8F5] rounded-xl border border-stone-200 space-y-3">
              <h4 className="font-bold text-stone-900 text-xs">Category Images (public storefront)</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categories.map((c) => (
                  <div key={c.id || c.name} className="flex items-start gap-2 bg-white p-2 rounded-lg border border-stone-200">
                    <div className="w-16 h-16 rounded-md border border-stone-200 overflow-hidden bg-[#FAF8F5] shrink-0">
                      {c.image_url ? (
                        <img src={resolveImageUrl(c.image_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[9px] text-stone-400 italic flex items-center justify-center h-full">No image</span>
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="text-xs font-bold text-stone-900 leading-tight">{c.name}</div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          data-testid={`admin-category-image-${c.id || c.slug}`}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const fd = new FormData();
                            fd.append("file", file);
                            try {
                              const { data: up } = await apiClient.post("/admin/upload-image", fd, {
                                headers: { "Content-Type": "multipart/form-data" },
                              });
                              await apiClient.patch(`/admin/categories/${c.id}`, { image_url: up.url });
                              toast.success(`${c.name} image updated`);
                              fetchAllAdminData();
                            } catch (err) {
                              toast.error("Upload failed");
                            }
                          }}
                        />
                        <span className="inline-block px-2.5 py-1 bg-white hover:bg-[#D4AF37]/10 border border-stone-300 rounded-md text-[10px] font-semibold text-stone-800 transition">
                          Change image
                        </span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Social links */}
            <div className="sm:col-span-2 p-4 bg-[#FAF8F5] rounded-xl border border-stone-200 space-y-3">
              <h4 className="font-bold text-stone-900 text-xs">Social Media Links</h4>
              <p className="text-[10px] text-stone-500 italic">
                Only platforms with a URL will show their icon in the footer. Leave empty to hide.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {["facebook","instagram","tiktok","youtube","twitter","whatsapp"].map((k) => (
                  <div key={k} className="space-y-1">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-stone-600 block">{k}</label>
                    <input
                      type="url"
                      value={settingsForm.social_links?.[k] || ""}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        social_links: { ...(settingsForm.social_links || {}), [k]: e.target.value },
                      })}
                      data-testid={`admin-settings-social-${k}`}
                      placeholder={`https://... (${k})`}
                      className="w-full px-2 py-1.5 bg-white border border-stone-300 rounded-lg text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Bank details */}
            <div className="sm:col-span-2 p-4 bg-[#FAF8F5] rounded-xl border border-stone-200 space-y-3">
              <h4 className="font-bold text-stone-900 text-xs">Bank Transfer Account (Meezan Bank):</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Bank Name"
                  value={settingsForm.bank_details?.bank_name || ""}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    bank_details: { ...settingsForm.bank_details, bank_name: e.target.value }
                  })}
                  className="px-3 py-1.5 bg-white border border-stone-300 rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Account Title"
                  value={settingsForm.bank_details?.account_title || ""}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    bank_details: { ...settingsForm.bank_details, account_title: e.target.value }
                  })}
                  className="px-3 py-1.5 bg-white border border-stone-300 rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Account Number"
                  value={settingsForm.bank_details?.account_number || ""}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    bank_details: { ...settingsForm.bank_details, account_number: e.target.value }
                  })}
                  className="px-3 py-1.5 bg-white border border-stone-300 rounded-lg"
                />
                <input
                  type="text"
                  placeholder="IBAN"
                  value={settingsForm.bank_details?.iban || ""}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    bank_details: { ...settingsForm.bank_details, iban: e.target.value }
                  })}
                  className="px-3 py-1.5 bg-white border border-stone-300 rounded-lg"
                />
              </div>
            </div>

            {/* EasyPaisa & JazzCash */}
            <div className="p-4 bg-emerald-50/70 rounded-xl border border-emerald-200 space-y-2">
              <h4 className="font-bold text-emerald-950 text-xs">EasyPaisa Account:</h4>
              <input
                type="text"
                placeholder="EasyPaisa Account Name"
                value={settingsForm.easypaisa_details?.account_name || ""}
                onChange={(e) => setSettingsForm({
                  ...settingsForm,
                  easypaisa_details: { ...settingsForm.easypaisa_details, account_name: e.target.value }
                })}
                className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-lg mb-2"
              />
              <input
                type="text"
                placeholder="EasyPaisa Mobile #"
                value={settingsForm.easypaisa_details?.account_number || ""}
                onChange={(e) => setSettingsForm({
                  ...settingsForm,
                  easypaisa_details: { ...settingsForm.easypaisa_details, account_number: e.target.value }
                })}
                className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-lg"
              />
            </div>

            <div className="p-4 bg-rose-50/70 rounded-xl border border-rose-200 space-y-2">
              <h4 className="font-bold text-rose-950 text-xs">JazzCash Account:</h4>
              <input
                type="text"
                placeholder="JazzCash Account Name"
                value={settingsForm.jazzcash_details?.account_name || ""}
                onChange={(e) => setSettingsForm({
                  ...settingsForm,
                  jazzcash_details: { ...settingsForm.jazzcash_details, account_name: e.target.value }
                })}
                className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-lg mb-2"
              />
              <input
                type="text"
                placeholder="JazzCash Mobile #"
                value={settingsForm.jazzcash_details?.account_number || ""}
                onChange={(e) => setSettingsForm({
                  ...settingsForm,
                  jazzcash_details: { ...settingsForm.jazzcash_details, account_number: e.target.value }
                })}
                className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-lg"
              />
            </div>

            {/* Delivery thresholds */}
            <div className="space-y-1">
              <label className="font-semibold text-stone-800">Standard Delivery Charge (PKR)</label>
              <input
                type="number"
                value={settingsForm.standard_delivery_charge || 250}
                onChange={(e) => setSettingsForm({ ...settingsForm, standard_delivery_charge: parseFloat(e.target.value) })}
                className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
              />
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-stone-800">Free Delivery Threshold (PKR)</label>
              <input
                type="number"
                value={settingsForm.free_delivery_threshold || 5000}
                onChange={(e) => setSettingsForm({ ...settingsForm, free_delivery_threshold: parseFloat(e.target.value) })}
                className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
              />
            </div>

          </div>

          <button
            type="submit"
            data-testid="admin-save-settings-btn"
            className="px-6 py-3 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            <span>Save Settings</span>
          </button>
        </form>
      )}

      {/* Product Add / Edit Modal */}
      {productModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-2xl w-full border border-[#D4AF37] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-[#1C1917] text-white p-5 flex items-center justify-between border-b-2 border-[#D4AF37]">
              <h3 className="font-heading font-bold text-lg">
                {editingProduct ? `Edit Product: ${editingProduct.name}` : "Add New Lace Product"}
              </h3>
              <button onClick={() => setProductModalOpen(false)} className="text-stone-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveProduct} className="p-6 space-y-4 overflow-y-auto text-xs">
              <div className="space-y-1">
                <label className="font-semibold text-stone-800">Product Title</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Imperial Gold Diamond Zari Sitara Set"
                  className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                  >
                    {categories.map((c) => (
                      <option key={c.id || c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">SKU Code</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">Retail Price (PKR per Set)</label>
                  <input
                    type="number"
                    required
                    value={formData.retail_price}
                    onChange={(e) => setFormData({ ...formData, retail_price: e.target.value })}
                    className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">Wholesale Price (PKR per Set)</label>
                  <input
                    type="number"
                    required
                    value={formData.wholesale_price}
                    onChange={(e) => setFormData({ ...formData, wholesale_price: e.target.value })}
                    className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-stone-800 flex items-center justify-between">
                  <span>Weight per Set (KG)</span>
                  <span className="text-[10px] text-stone-400 italic">Used for delivery bracket calculation</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  required
                  value={formData.weight_kg}
                  onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                  data-testid="admin-product-weight"
                  placeholder="e.g. 0.5"
                  className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">Stock Status</label>
                  <select
                    value={formData.stock_status}
                    onChange={(e) => setFormData({ ...formData, stock_status: e.target.value })}
                    className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                  >
                    <option value="In Stock">In Stock</option>
                    <option value="Low Stock">Low Stock</option>
                    <option value="Made to Order">Made to Order</option>
                    <option value="Out of Stock">Out of Stock</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">Stock Quantity</label>
                  <input
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">Min Wholesale Qty</label>
                  <input
                    type="number"
                    value={formData.min_wholesale_qty}
                    onChange={(e) => setFormData({ ...formData, min_wholesale_qty: e.target.value })}
                    className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-stone-800">Available Colors (Comma Separated)</label>
                <input
                  type="text"
                  value={formData.available_colors}
                  onChange={(e) => setFormData({ ...formData, available_colors: e.target.value })}
                  placeholder="e.g. Fawn, Powder Pink, Champagne Gold"
                  className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-stone-800 flex items-center justify-between">
                  <span>Design Code / Number</span>
                  <span className="text-[10px] text-stone-400 italic">One code per product only</span>
                </label>
                <input
                  type="text"
                  value={formData.design_code}
                  onChange={(e) => setFormData({ ...formData, design_code: e.target.value })}
                  data-testid="admin-product-design-code"
                  placeholder="e.g. AF-514"
                  className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                />
              </div>

              <div className="space-y-2 pt-2 border-t border-stone-200">
                <label className="font-semibold text-stone-800 block">Complete Set Details</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500 block">Total Colors</label>
                    <input
                      type="number" min="0"
                      value={formData.complete_set.total_colors}
                      onChange={(e) => setFormData({ ...formData, complete_set: { ...formData.complete_set, total_colors: e.target.value } })}
                      data-testid="admin-cs-colors"
                      className="w-full px-2 py-1.5 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500 block">Taan Count</label>
                    <input
                      type="number" min="0"
                      value={formData.complete_set.taan_count}
                      onChange={(e) => setFormData({ ...formData, complete_set: { ...formData.complete_set, taan_count: e.target.value } })}
                      data-testid="admin-cs-taan"
                      className="w-full px-2 py-1.5 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500 block">Gaz per Taan</label>
                    <input
                      type="number" min="0" step="0.1"
                      value={formData.complete_set.gaz_per_taan}
                      onChange={(e) => setFormData({ ...formData, complete_set: { ...formData.complete_set, gaz_per_taan: e.target.value } })}
                      data-testid="admin-cs-gaz-per-taan"
                      className="w-full px-2 py-1.5 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500 block">Total Gaz</label>
                    <input
                      type="number" min="0" step="0.1"
                      value={formData.complete_set.total_gaz}
                      onChange={(e) => setFormData({ ...formData, complete_set: { ...formData.complete_set, total_gaz: e.target.value } })}
                      data-testid="admin-cs-total-gaz"
                      placeholder={String((parseInt(formData.complete_set.taan_count) || 0) * (parseFloat(formData.complete_set.gaz_per_taan) || 0))}
                      className="w-full px-2 py-1.5 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500 block">Min. Order (Sets)</label>
                    <input
                      type="number" min="1"
                      value={formData.complete_set.min_order_sets}
                      onChange={(e) => setFormData({ ...formData, complete_set: { ...formData.complete_set, min_order_sets: e.target.value } })}
                      data-testid="admin-cs-min-sets"
                      className="w-full px-2 py-1.5 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs"
                    />
                  </div>
                </div>
                <textarea
                  rows={2}
                  value={formData.complete_set.notes}
                  onChange={(e) => setFormData({ ...formData, complete_set: { ...formData.complete_set, notes: e.target.value } })}
                  placeholder="Optional notes about this complete set (visible to customer)"
                  className="w-full px-2 py-1.5 mt-1 bg-[#FAF8F5] border border-stone-300 rounded-lg text-xs resize-none"
                />
              </div>

              <div className="space-y-2">
                <label className="font-semibold text-stone-800 flex items-center justify-between">
                  <span>Product Images ({formData.images?.length || 0})</span>
                  {uploadingImage && <span className="text-[10px] text-[#996515]">Uploading…</span>}
                </label>
                <div
                  onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  data-testid="admin-image-dropzone"
                  className={`relative border-2 border-dashed rounded-xl p-5 text-center transition ${
                    dragActive
                      ? "border-[#D4AF37] bg-[#D4AF37]/5"
                      : "border-stone-300 bg-[#FAF8F5] hover:border-[#D4AF37]"
                  }`}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    data-testid="admin-image-file-input"
                    onChange={(e) => uploadFiles(e.target.files)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    aria-label="Upload product images"
                  />
                  <UploadCloud className="w-8 h-8 text-[#996515] mx-auto mb-1" />
                  <p className="text-xs font-semibold text-stone-800">
                    Drag &amp; drop images here, or click to browse
                  </p>
                  <p className="text-[10px] text-stone-500 mt-0.5">
                    JPG · PNG · WEBP · GIF · Max 8&nbsp;MB per image
                  </p>
                </div>
                {formData.images?.length > 0 && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 pt-2">
                    {formData.images.map((img, idx) => (
                      <div
                        key={`${img}-${idx}`}
                        data-testid={`admin-image-thumb-${idx}`}
                        className="relative group aspect-square rounded-lg overflow-hidden border border-stone-200 bg-white"
                      >
                        <img
                          src={resolveImageUrl(img)}
                          alt={`Image ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          data-testid={`admin-image-remove-${idx}`}
                          className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                          aria-label="Remove image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {idx === 0 && (
                          <span className="absolute bottom-1 left-1 text-[9px] bg-[#D4AF37] text-[#1C1917] font-bold px-1.5 py-0.5 rounded">
                            Primary
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-stone-800">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
                />
              </div>

              <div className="space-y-2 pt-2 border-t border-stone-200">
                <label className="font-semibold text-stone-800 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <TagIcon className="w-3.5 h-3.5 text-[#D4AF37]" />
                    Bulk Wholesale Pricing Tiers
                  </span>
                  <button
                    type="button"
                    onClick={handleAddTier}
                    data-testid="admin-add-tier-btn"
                    className="text-[10px] font-bold text-[#996515] hover:text-[#1C1917] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/30 px-2 py-1 rounded"
                  >
                    + Add Tier
                  </button>
                </label>
                <p className="text-[10px] text-stone-500">
                  Wholesale orders qualifying for a tier will automatically get the tier price. Leave empty to use the default wholesale price.
                </p>
                {(formData.pricing_tiers || []).length === 0 ? (
                  <p className="text-[10px] text-stone-400 italic bg-stone-50 border border-dashed border-stone-200 rounded-lg p-3 text-center">
                    No bulk tiers configured. Click "+ Add Tier" to offer volume discounts.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {formData.pricing_tiers.map((t, idx) => (
                      <div
                        key={idx}
                        data-testid={`admin-tier-row-${idx}`}
                        className="grid grid-cols-12 gap-2 items-center bg-[#FAF8F5] p-2 rounded-lg border border-stone-200"
                      >
                        <div className="col-span-3">
                          <label className="text-[9px] uppercase tracking-wider text-stone-500 block">Min Sets</label>
                          <input
                            type="number"
                            min="1"
                            value={t.min_quantity}
                            onChange={(e) => handleUpdateTier(idx, "min_quantity", e.target.value)}
                            data-testid={`admin-tier-min-${idx}`}
                            className="w-full px-2 py-1 bg-white border border-stone-300 rounded text-xs"
                          />
                        </div>
                        <div className="col-span-4">
                          <label className="text-[9px] uppercase tracking-wider text-stone-500 block">Price / Set (PKR)</label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={t.price_per_set}
                            onChange={(e) => handleUpdateTier(idx, "price_per_set", e.target.value)}
                            data-testid={`admin-tier-price-${idx}`}
                            className="w-full px-2 py-1 bg-white border border-stone-300 rounded text-xs"
                          />
                        </div>
                        <div className="col-span-4">
                          <label className="text-[9px] uppercase tracking-wider text-stone-500 block">Label (optional)</label>
                          <input
                            type="text"
                            placeholder={`${t.min_quantity}+ Sets`}
                            value={t.label || ""}
                            onChange={(e) => handleUpdateTier(idx, "label", e.target.value)}
                            className="w-full px-2 py-1 bg-white border border-stone-300 rounded text-xs"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveTier(idx)}
                          data-testid={`admin-tier-remove-${idx}`}
                          className="col-span-1 self-end p-1.5 text-stone-400 hover:text-rose-600 rounded"
                          aria-label="Remove tier"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_complete_set}
                    onChange={(e) => setFormData({ ...formData, is_complete_set: e.target.checked })}
                    className="rounded text-[#D4AF37]"
                  />
                  <span className="font-bold text-stone-900">Complete Set Pack</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="rounded text-[#D4AF37]"
                  />
                  <span className="font-bold text-stone-900">Featured Bestseller</span>
                </label>
              </div>

              <div className="pt-4 border-t border-stone-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setProductModalOpen(false)}
                  className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="modal-save-product-btn"
                  className="px-6 py-2 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] font-bold rounded-lg transition"
                >
                  Save Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
